import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Brain } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { generateTalentAnalysisReport, StreamCallbacks } from '../services/talentAnalysis';

const TalentTestResult: React.FC = () => {
  const [analysisReport, setAnalysisReport] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [position, setPosition] = useState('');
  const [testCode, setTestCode] = useState('');
  const formRef = useRef<HTMLDivElement | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  // 流式生成相关状态（复用情绪记忆页面的方案）
  const [streamContent, setStreamContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  // 统一开发与生产：默认不展示主“重新生成报告”按钮，仅提供次级入口在报告标题附近
  const location = useLocation();
  const locationState = location.state as { sortedTalents: [string, number][] } | undefined;
  // 优先使用导航传入的结果；若没有，则尝试从本地缓存恢复
  const savedSortedTalentsStr = (() => {
    try { return localStorage.getItem('talent_last_sortedTalents') || ''; } catch { return ''; }
  })();
  const savedSortedTalents: [string, number][] = savedSortedTalentsStr ? JSON.parse(savedSortedTalentsStr) : [];
  const sortedTalents: [string, number][] = locationState?.sortedTalents ?? savedSortedTalents;

  const noResults = !sortedTalents || sortedTalents.length === 0;

  const top10 = sortedTalents.slice(0, 10).map(t => t[0]);
  const bottom5 = sortedTalents.slice(-5).map(t => t[0]);

  const handleShowInfoForm = () => {
    setShowInfoForm(true);
  };

  // 启动时恢复上次报告、岗位、测评码，便于刷新/重启后直接查看
  useEffect(() => {
    try {
      const lastReport = localStorage.getItem('talent_last_analysis_report');
      const lastPosition = localStorage.getItem('talent_last_position');
      const lastTestCode = localStorage.getItem('talent_last_testCode');
      if (lastReport) setAnalysisReport(lastReport);
      if (lastPosition) setPosition(lastPosition);
      if (lastTestCode) setTestCode(lastTestCode);
    } catch (e) {
      console.warn('无法恢复本地缓存（报告/岗位/测评码）:', e);
    }
  }, []);

  // 当需要填写信息时，自动滚动到信息表单位置
  useEffect(() => {
    if (showInfoForm) {
      // 等待DOM更新后再滚动，避免未渲染时无效
      requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [showInfoForm]);

  const handleGenerateReport = async () => {
    setError("");
    setIsLoading(true);
    setAnalysisReport("");
    setStreamContent('');
    setIsStreaming(false);
    setShowCursor(false);
    let teamPrompt = "";
    try {
      // 调用后台接口获取公司列表
      const response = await fetch("/api/team-admin");
      if (!response.ok) {
        throw new Error("无法获取公司列表");
      }
      const companies = await response.json();
      // 统一比较：去除前后空格并大写，避免大小写或误输入空格导致匹配失败
      const inputCode = (testCode || '').trim().toUpperCase();
      const matchedCompany = companies.find((c: any) => ((c?.testCode || '').trim().toUpperCase()) === inputCode);
      if (!matchedCompany) {
        setError("测评码不正确，请重新输入");
        setIsLoading(false);
        return;
      }
      teamPrompt = matchedCompany.prompt || "";
      if (!position) {
        setError("请填写岗位信息。");
        setIsLoading(false);
        return;
      }
      setShowInfoForm(false);
      console.log("开始生成才干分析报告（流式）...");
      const callbacks: StreamCallbacks = {
        onStart: () => {
          // 不在 onStart 关闭加载，等待首个非空 token 再切换到蒙版效果
          setAnalysisReport('');
          setStreamContent('');
        },
        onToken: (token) => {
          if (!token) return;
          setStreamContent(prev => {
            const next = prev + token;
            // 首个非空 token 到达时：关闭加载框，开启蒙版与光标效果
            if (prev.length === 0 && token.trim() !== '') {
              setIsLoading(false);
              setIsStreaming(true);
              setShowCursor(true);
            }
            return next;
          });
        },
        onComplete: (fullText) => {
          console.log("才干分析报告生成完毕（流式完成）");
          setIsStreaming(false);
          setShowCursor(false);
          // 统一显示源为 streamContent，避免完成后再次切换显示源导致“重新输出”的主观感受
          setStreamContent(fullText);
          // 同步到 analysisReport，仅用于缓存与非流式回显
          setAnalysisReport(fullText);
          // 写入本地缓存，便于刷新后查看
          try {
            localStorage.setItem('talent_last_analysis_report', fullText);
            localStorage.setItem('talent_last_position', position);
            localStorage.setItem('talent_last_testCode', testCode);
            localStorage.setItem('talent_last_top10', JSON.stringify(top10));
            localStorage.setItem('talent_last_bottom5', JSON.stringify(bottom5));
            localStorage.setItem('talent_last_generatedAt', Date.now().toString());
          } catch (e) {
            console.warn('无法写入本地缓存（报告/岗位/测评码）:', e);
          }
        },
        onError: (err) => {
          console.error('生成才干分析报告失败（流式）:', err);
          setIsStreaming(false);
          setIsLoading(false);
          setShowCursor(false);
          setError(`生成才干分析报告失败：${(err as Error).message || '网络或服务器错误'}`);
        }
      };

      await generateTalentAnalysisReport({ top10, bottom5, prompt: teamPrompt, position }, callbacks);
    } catch (err) {
      setError("网络或服务器错误，请稍后再试。");
      setIsLoading(false);
    }
  };

  const handleCopyReport = async () => {
    try {
      if (analysisReport) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(analysisReport);
        } else {
          // 兼容旧浏览器：回退到选择复制
          const textarea = document.createElement('textarea');
          textarea.value = analysisReport;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        // 用户要求：在中间显示一个小的反馈提示
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 1800);
      }
    } catch (e) {
      console.warn('复制失败', e);
      // 即便失败也给出简短反馈，避免静默：
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 1200);
    }
  };

  const handlePrintPDF = () => {
    // 方案A：调用浏览器原生打印为 PDF。我们通过 print:hidden 隐藏交互元素，保留才干排序与报告
    window.print();
  };

  return (
    <div className="bg-gray-100 p-5 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {!noResults && (
          <h2 className="text-3xl font-bold text-center mb-8">你的34才干排序</h2>
        )}
        {/* 打印优化：改为四列，保持与网页一致的视觉；同时缩小间距与字号以提高密度 */}
        <div className="grid md:grid-cols-4 gap-6 print:grid-cols-4 print:gap-4">
          {!noResults && (
          <div className="bg-white rounded-lg shadow-sm p-4 print:shadow-none print:border print:border-gray-300 print:p-3" style={{ breakInside: 'avoid' }}>
            <h3 className="text-xl font-semibold mb-4 text-center print:text-base print:mb-2">才干排序 (1-10)</h3>
            <ul className="space-y-3 print:space-y-1">
              {top10.map((talent, index) => (
                <li key={talent} className="p-3 bg-primary/10 text-primary-dark rounded-lg shadow-sm text-center print:p-2 print:text-xs">{index + 1}. {talent}</li>
              ))}
            </ul>
          </div>
          )}
          {!noResults && (
          <div className="bg-white rounded-lg shadow-sm p-4 print:shadow-none print:border print:border-gray-300 print:p-3" style={{ breakInside: 'avoid' }}>
            <h3 className="text-xl font-semibold mb-4 text-center print:text-base print:mb-2">才干排序 (11-20)</h3>
            <ul className="space-y-3 print:space-y-1">
              {sortedTalents.slice(10, 20).map(([talent], index) => (
                <li key={talent} className="p-3 bg-neutral-100 rounded-lg shadow-sm text-center print:p-2 print:text-xs">{index + 11}. {talent}</li>
              ))}
            </ul>
          </div>
          )}
          {!noResults && (
          <div className="bg-white rounded-lg shadow-sm p-4 print:shadow-none print:border print:border-gray-300 print:p-3" style={{ breakInside: 'avoid' }}>
            <h3 className="text-xl font-semibold mb-4 text-center print:text-base print:mb-2">才干排序 (21-29)</h3>
            <ul className="space-y-3 print:space-y-1">
              {sortedTalents.slice(20, 29).map(([talent], index) => (
                <li key={talent} className="p-3 bg-neutral-100 rounded-lg shadow-sm text-center print:p-2 print:text-xs">{index + 21}. {talent}</li>
              ))}
            </ul>
          </div>
          )}
          {!noResults && (
          <div className="bg-white rounded-lg shadow-sm p-4 print:shadow-none print:border print:border-gray-300 print:p-3" style={{ breakInside: 'avoid' }}>
            <h3 className="text-xl font-semibold mb-4 text-center print:text-base print:mb-2">才干排序 (30-34)</h3>
            <ul className="space-y-3 print:space-y-1">
              {bottom5.map((talent, index) => (
                <li key={talent} className="p-3 bg-neutral-200 rounded-lg shadow-sm text-center print:p-2 print:text-xs">{sortedTalents.length - 5 + index + 1}. {talent}</li>
              ))}
            </ul>
          </div>
          )}
        </div>

        {noResults && (
          <div className="talent-test-page-container text-center">
            <h2 className="text-2xl font-bold mb-4">没有可用的结果</h2>
            <p className="mb-8">似乎没有测试结果数据，请先完成才干测试。</p>
            <Link to="/talent-test-intro" className="btn-primary" style={{ textDecoration: 'none' }}>
              返回测试介绍页
            </Link>
          </div>
        )}

        <div className="mt-12 space-y-6">
          <a href="/personality-dictionary" target="_blank" rel="noopener noreferrer" className="block p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center text-xl font-semibold text-gray-800 print:hidden" style={{ textDecoration: 'none' }}>
            查看34才干词典
          </a>

          {!analysisReport && !isLoading && !showInfoForm && (
            <div onClick={handleShowInfoForm} className="block p-6 bg-gradient-to-r from-accent-yellow-light to-accent-yellow text-black rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center text-xl font-semibold cursor-pointer">
              输入更多信息获得定制报告
            </div>
          )}
          {/* 开发与生产一致：不在此处展示重生按钮，改为在报告标题附近提供次级入口 */}
        </div>

        {showInfoForm && (
          <div ref={formRef} className="mt-8 p-8 bg-white rounded-xl shadow-lg space-y-6 print:hidden">
            <h3 className="text-2xl font-bold text-center text-gray-800">请输入更多信息以生成定制报告</h3>
            


            <div>
              <label htmlFor="testCode" className="block text-sm font-medium text-gray-700 mb-2">团队测评码</label>
              <input
                type="password"
                id="testCode"
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary transition duration-300"
                placeholder="请输入测评码"
              />
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">你的岗位</label>
              <input
                type="text"
                id="position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary transition duration-300"
                placeholder="例如：首席产品体验官"
              />
            </div>

            <div className="text-center">
              <button onClick={handleGenerateReport} className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-accent-yellow-light to-accent-yellow text-black rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 font-semibold" disabled={isLoading}>
                {isLoading ? '正在生成...' : '确认并生成报告'}
              </button>
              {error && <p className="text-gray-500 text-sm mt-4">{error}</p>}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="mt-12 p-8 bg-white rounded-lg shadow-lg min-h-[420px] flex flex-col items-center justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-gray-300 border-t-accent-yellow animate-spin mb-4" aria-label="loading" />
            <p className="text-gray-700">正在准备生成报告...</p>
          </div>
        )}

        {(analysisReport || streamContent) && (
          <div className="mt-12 p-8 bg-white rounded-lg shadow-lg letter-paper prose lg:prose-xl max-w-none">
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-center">你的个人才干分析报告</h3>
            </div>
            {/* 报告内容（支持流式） */}
            {(() => {
              const displayContent = streamContent || analysisReport;
              return (
                <div className="relative">
                  {isStreaming && (
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10 overflow-hidden not-prose">
                      <div 
                        className="flex flex-col items-center justify-center mx-auto"
                        style={{ position: 'relative', top: '30%' }}
                      >
                        <motion.div
                          animate={{ scale: [0.85, 1.0, 1.25, 1.0] }}
                          transition={{ repeat: Infinity, duration: 2.8, ease: "easeInOut" }}
                          style={{
                            background: 'linear-gradient(45deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.24) 100%)',
                            borderRadius: '50%',
                            padding: '8px',
                            marginBottom: '8px',
                            boxShadow: '0 0 12px rgba(245,158,11,0.35)'
                          }}
                        >
                          <Brain size={40} color="#F59E0B" />
                        </motion.div>
                        <div className="text-center text-gray-700 text-base font-normal">
                          正在努力生成报告...
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`transition-all duration-500 ${isStreaming ? 'filter blur-sm' : ''}`}>
                    <ReactMarkdown>
                      {displayContent}
                    </ReactMarkdown>
                    {isStreaming && showCursor && (
                      <span className="inline-block h-5 w-0.5 bg-[#4A4A4A] ml-1 animate-pulse"></span>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="mt-8 flex items-center justify-between print:hidden">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCopyReport}
                  disabled={isStreaming}
                  className="px-3 py-2 text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-md border border-neutral-300"
                >
                  一键复制报告内容
                </button>
                <button
                  onClick={handlePrintPDF}
                  disabled={isStreaming}
                  className="px-3 py-2 text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-md border border-neutral-300"
                >
                  下载 PDF
                </button>
              </div>
              <div className="text-right">
                <button
                  onClick={() => setShowInfoForm(true)}
                  className="px-4 py-2 text-sm text-neutral-600 underline hover:text-neutral-800"
                >
                  报告出现问题？点击这里重新生成
                </button>
              </div>
            </div>
            {/* 复制后的居中提示（屏幕可见，打印隐藏） */}
            {showCopyToast && (
              <div className="fixed inset-0 flex items-center justify-center pointer-events-none print:hidden">
                <div className="px-3 py-2 rounded-md bg-neutral-800/85 text-white text-xs shadow-lg">
                  已复制
                </div>
              </div>
            )}
            {/* 打印品牌露出：位于正文底部的一行居中文案，屏幕隐藏、打印显示 */}
            <div className="hidden print:block text-center text-gray-600 text-xs mt-8">
              人格向量实验室 · personality-vector-lab.com
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default TalentTestResult;